import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { Instance } from '../api/client'
import { useLabels, useSetInstanceLabel } from '../hooks/useLabels'

// Small chip + dropdown to assign (or clear) an instance's label. Used across
// card, list and kanban views.
export default function LabelPicker({ instance, onManage }: { instance: Instance; onManage?: () => void }) {
  const { t } = useTranslation()
  const { data: labels } = useLabels()
  const setLabel = useSetInstanceLabel()
  const [open, setOpen] = useState(false)

  const current = instance.label

  const pick = (labelId: string | null) => {
    setOpen(false)
    if ((current?.id ?? null) === labelId) return
    setLabel.mutate({ instanceId: instance.id, labelId })
  }

  return (
    <div className="relative" onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex items-center gap-1.5 max-w-[160px] rounded-full border px-2 py-0.5 text-xs font-medium transition-colors hover:bg-gray-50 ${
          current ? '' : 'border-gray-200 text-gray-400'
        }`}
        style={current ? { borderColor: current.color, color: current.color } : undefined}
        title={t('dashboard.card.assign')}
      >
        <span
          className={`h-2 w-2 shrink-0 rounded-full ${current ? '' : 'bg-gray-300'}`}
          style={current ? { backgroundColor: current.color } : undefined}
        />
        <span className="truncate">{current?.name ?? t('dashboard.card.assign')}</span>
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 z-20 mt-1 w-48 rounded-lg border border-gray-200 bg-white py-1 shadow-lg">
            <button
              onClick={() => pick(null)}
              className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs hover:bg-gray-50 ${!current ? 'font-semibold text-gray-900' : 'text-gray-500'}`}
            >
              <span className="h-2 w-2 rounded-full bg-gray-300" />
              {t('dashboard.card.none')}
            </button>
            {(labels ?? []).map((l) => (
              <button
                key={l.id}
                onClick={() => pick(l.id)}
                className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs hover:bg-gray-50 ${current?.id === l.id ? 'font-semibold text-gray-900' : 'text-gray-600'}`}
              >
                <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: l.color }} />
                <span className="truncate">{l.name}</span>
              </button>
            ))}
            {onManage && (
              <button
                onClick={() => { setOpen(false); onManage() }}
                className="mt-1 w-full border-t border-gray-100 px-3 py-1.5 text-left text-xs text-brand-600 hover:bg-gray-50"
              >
                {t('dashboard.labels.manage')}
              </button>
            )}
          </div>
        </>
      )}
    </div>
  )
}
